// FE-SCRM-050 — Prospect detail page.
//
// Route: ``/dashboard/sales-crm/prospects/:prospectId``.
//
// Architectural mirror of OpportunityDetailPage (FE-SCRM-011): a
// narrow header up top + lazy-loaded sections below. Sections are
// independent React Query roots so a slow one doesn't gate the rest
// of the page.
//
// What lands:
//   * Header (name + chips + archive badge + archive action).
//   * Overview card (read-only field grid + inline TagsInput).
//   * Benefits intelligence panel (wired to the new
//     ``/api/v1/sales/prospects/{id}/benefits-intelligence`` endpoint).
//   * Prospect stakeholder summary (aggregated across opportunities).
//   * Sales contacts list + "+ Add contact" modal.
//   * Opportunities list with "+ New opportunity" CTA placeholder —
//     FE-SCRM-051 wires the actual AddOpportunityModal.
//   * Activity rail bound to ``prospect_id``.
//   * Related commitments section (empty state until commitments app
//     surfaces a ``sales_prospect`` filter).
//   * Prospect alerts (DATA-SCRM-019).

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Toast } from "@/design-system";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { AddOpportunityModal } from "../AddOpportunity";
import { BenefitsIntelligencePanel } from "../BenefitsIntelligence";
import { ActivityRail } from "../activities";
import { TagsInput } from "../components";

import { AddContactModal } from "./AddContactModal";
import { LinkForm5500SponsorModal } from "./LinkForm5500SponsorModal";
import { ProspectAlertsSection } from "./ProspectAlertsSection";
import { ProspectStakeholderSection } from "./ProspectStakeholderSection";
import {
  useArchiveProspectMutation,
  useCreateProspectContactMutation,
  useLinkForm5500SponsorMutation,
  usePatchProspectTagsMutation,
  useProspectBenefitsIntelligenceQuery,
  useProspectContactsQuery,
  useProspectDetailQuery,
  useProspectOpportunitiesQuery,
  useUnlinkForm5500SponsorMutation,
} from "./hooks";
import {
  fetchProspectTagSuggestions, searchForm5500Sponsors,
} from "./api";
import type { Form5500SponsorSuggestion, ProspectDetail } from "./types";


const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const SOURCE_LABEL: Record<string, string> = {
  carrier_referral: "Carrier referral",
  account_referral: "Account referral",
  contact_referral: "Contact referral",
  cold_outbound: "Cold outbound",
  inbound_marketing: "Inbound marketing",
  rfp_invitation: "RFP invitation",
  conference_event: "Conference / event",
  other: "Other",
};


export function ProspectDetailPage() {
  const { prospectId } = useParams();
  const detail = useProspectDetailQuery(prospectId);

  if (!prospectId) {
    return <NotFoundView />;
  }

  if (detail.isLoading) {
    return (
      <div
        data-testid="prospect-detail-page"
        className="flex flex-col gap-5"
      >
        <DashboardLoadingState variant="card" />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return <NotFoundView />;
  }

  return (
    <ProspectDetailContent prospect={detail.data} />
  );
}


function ProspectDetailContent({ prospect }: { prospect: ProspectDetail }) {
  const navigate = useNavigate();
  const archive = useArchiveProspectMutation(prospect.prospect_id);
  const [toast, setToast] = useState<
    | null
    | { variant: "success" | "error"; title: string }
  >(null);

  function handleArchive() {
    if (prospect.status === "archived") return;
    archive.mutate(undefined, {
      onSuccess: () => {
        setToast({
          variant: "success",
          title: `Archived ${prospect.name}`,
        });
        // Spec: "routes back to the Prospects list with a toast."
        // Prospects-tab anchor lives at /dashboard/sales-crm?tab=prospects.
        setTimeout(() => {
          navigate("/dashboard/sales-crm?tab=prospects");
        }, 600);
      },
      onError: () =>
        setToast({
          variant: "error",
          title: "Couldn't archive — try again.",
        }),
    });
  }

  return (
    <div
      data-testid="prospect-detail-page"
      className="flex flex-col gap-5"
    >
      <Header
        prospect={prospect}
        on_archive={handleArchive}
        archive_pending={archive.isPending}
      />

      <OverviewCard prospect={prospect} />

      <main className="grid grid-cols-1 gap-5 xl:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-5">
          <IntelligenceSection
            prospect={prospect}
            onToast={setToast}
          />
          <ProspectStakeholderSection prospect_id={prospect.prospect_id} />
          <ContactsSection prospect_id={prospect.prospect_id} />
          <OpportunitiesSection prospect={prospect} />
          <RelatedCommitmentsSection />
        </div>

        <aside className="flex flex-col gap-5">
          <section
            data-testid="prospect-activity-rail-card"
            className="rounded-md border border-border-subtle bg-raised p-4"
          >
            <ActivityRail prospect_id={prospect.prospect_id} />
          </section>
          <ProspectAlertsSection prospect_id={prospect.prospect_id} />
        </aside>
      </main>

      {toast && (
        <Toast
          variant={toast.variant}
          title={toast.title}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}


// ── Header ───────────────────────────────────────────────────────────────


function Header({
  prospect, on_archive, archive_pending,
}: {
  prospect: ProspectDetail;
  on_archive: () => void;
  archive_pending: boolean;
}) {
  const renewal_label =
    prospect.renewal_month && prospect.renewal_year
      ? `${MONTH_NAMES[prospect.renewal_month - 1]} ${prospect.renewal_year}`
      : null;
  const lives_label = prospect.lives_band
    ? lives_band_human(prospect.lives_band, prospect.estimated_lives)
    : null;
  const source_label = SOURCE_LABEL[prospect.source] ?? prospect.source;
  const archived = prospect.status === "archived";

  return (
    <header className="flex flex-col gap-2">
      <Link
        to="/dashboard/sales-crm?tab=prospects"
        className="text-xs text-text-tertiary hover:text-text-primary"
      >
        ← Back to Prospects
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-lg font-semibold tracking-tight text-text-primary">
            {prospect.name}
          </h1>
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            {renewal_label && (
              <Chip data-testid="prospect-renewal-chip">{renewal_label}</Chip>
            )}
            {lives_label && (
              <Chip data-testid="prospect-lives-chip">{lives_label}</Chip>
            )}
            <Chip data-testid="prospect-source-chip">{source_label}</Chip>
            {archived && (
              <Chip
                data-testid="prospect-archived-badge"
                tone="warning"
              >
                Archived
              </Chip>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="prospect-archive-button"
            disabled={archived || archive_pending}
            onClick={on_archive}
            className={[
              "rounded-md border px-3 py-1.5 text-sm",
              archived || archive_pending
                ? "cursor-not-allowed border-border-subtle text-text-tertiary"
                : "border-border-subtle text-text-secondary hover:bg-recessed",
            ].join(" ")}
          >
            {archive_pending ? "Archiving…" : archived ? "Archived" : "Archive"}
          </button>
        </div>
      </div>
    </header>
  );
}


function Chip({
  children, tone = "default", ...rest
}: {
  children: React.ReactNode;
  tone?: "default" | "warning";
} & React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      {...rest}
      className={[
        "rounded-full px-2 py-0.5 uppercase tracking-wide",
        tone === "warning"
          ? "bg-amber-soft text-amber-strong"
          : "bg-recessed text-text-tertiary",
      ].join(" ")}
    >
      {children}
    </span>
  );
}


function lives_band_human(band: string, count: number | null): string {
  const band_label =
    band === "micro" ? "Micro group"
      : band === "small" ? "Small group"
        : band === "mid_market" ? "Mid-market"
          : band === "large" ? "Large group"
            : band === "jumbo" ? "Jumbo"
              : band;
  if (count !== null) {
    return `${band_label} · ${count.toLocaleString()} lives`;
  }
  return band_label;
}


// ── Overview card ────────────────────────────────────────────────────────


function OverviewCard({ prospect }: { prospect: ProspectDetail }) {
  const patchTags = usePatchProspectTagsMutation(prospect.prospect_id);
  const [optimistic_tags, setOptimisticTags] = useState<
    string[] | null
  >(null);
  const tags = optimistic_tags ?? prospect.tags;

  return (
    <section
      data-testid="prospect-overview-card"
      className="rounded-md border border-border-subtle bg-raised p-4"
    >
      <header className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-text-primary">
          Overview
        </h2>
      </header>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm md:grid-cols-2">
        <FieldRow label="EIN" value={prospect.ein} />
        <FieldRow label="Industry" value={prospect.industry} />
        <FieldRow
          label="Address"
          value={
            [
              prospect.address, prospect.city, prospect.state,
              prospect.postal_code,
            ]
              .filter(Boolean)
              .join(", ") || null
          }
        />
        <FieldRow label="Website" value={prospect.website} />
        <FieldRow
          label="Current broker"
          value={prospect.current_broker}
        />
        <FieldRow
          label="Broker relationship"
          value={prospect.current_broker_relationship_strength.replaceAll("_", " ")}
        />
        <FieldRow
          label="Employee count"
          value={
            prospect.estimated_lives !== null
              ? prospect.estimated_lives.toLocaleString()
              : null
          }
        />
        <FieldRow
          label="Renewal confidence"
          value={prospect.renewal_confidence.replaceAll("_", " ")}
        />
      </dl>

      <div
        className="mt-4 border-t border-border-subtle pt-3"
        data-testid="prospect-overview-tags"
      >
        <span className="text-xs font-medium text-text-secondary">
          Tags
        </span>
        <div className="mt-1">
          <TagsInput
            value={tags}
            onChange={(next) => {
              setOptimisticTags(next);
              patchTags.mutate(
                { tags: next },
                {
                  onSettled: (data) => {
                    // Server-confirmed list wins — wipes the optimistic
                    // override so a server-side normalization (e.g.
                    // tag-name capitalization) reaches the UI.
                    if (data) setOptimisticTags(null);
                  },
                  onError: () => setOptimisticTags(null),
                },
              );
            }}
            fetchSuggestions={fetchProspectTagSuggestions}
            placeholder="Add a tag…"
          />
        </div>
      </div>

      <div
        className="mt-4 border-t border-border-subtle pt-3"
        data-testid="prospect-overview-notes"
      >
        <span className="text-xs font-medium text-text-secondary">
          Notes
        </span>
        {/* Notes column lives on the model spec but is not yet
            exposed by the prospect serializer / PATCH path. The
            inline-edit affordance lands alongside the BE add in a
            v1.5 follow-up; rendering a placeholder keeps the
            section in the right place visually. */}
        <p className="mt-1 text-sm italic text-text-tertiary">
          Notes coming soon.
        </p>
      </div>
    </section>
  );
}


function FieldRow({
  label, value,
}: {
  label: string;
  value: string | null;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-text-tertiary">
        {label}
      </dt>
      <dd className="text-sm text-text-primary">
        {value ?? <span className="text-text-tertiary">—</span>}
      </dd>
    </div>
  );
}


// ── Sections ─────────────────────────────────────────────────────────────


type ToastState = { variant: "success" | "error"; title: string };

function IntelligenceSection({
  prospect, onToast,
}: {
  prospect: ProspectDetail;
  onToast: (t: ToastState) => void;
}) {
  const query = useProspectBenefitsIntelligenceQuery(prospect.prospect_id);
  const link_mutation = useLinkForm5500SponsorMutation(prospect.prospect_id);
  const unlink_mutation = useUnlinkForm5500SponsorMutation(prospect.prospect_id);
  const [link_modal_open, setLinkModalOpen] = useState(false);
  const [unlink_confirm_open, setUnlinkConfirmOpen] = useState(false);

  const has_linked_sponsor = prospect.form5500_sponsor_id !== null;

  async function handleLink(sponsor: Form5500SponsorSuggestion) {
    await link_mutation.mutateAsync(sponsor);
    setLinkModalOpen(false);
    onToast({
      variant: "success",
      title: `Linked to ${sponsor.name}.`,
    });
  }

  function handleUnlinkConfirm() {
    setUnlinkConfirmOpen(false);
    unlink_mutation.mutate(undefined, {
      onSuccess: () =>
        onToast({
          variant: "success",
          title: "Form 5500 sponsor unlinked.",
        }),
      onError: () =>
        onToast({
          variant: "error",
          title: "Couldn't unlink — try again.",
        }),
    });
  }

  return (
    <>
      <BenefitsIntelligencePanel
        data={query.data ?? null}
        isLoading={query.isLoading}
        // The link CTA only makes sense when the prospect has no
        // sponsor. With a sponsor linked we let the panel render
        // its populated state (and the host-supplied unlink link
        // in the header lets the producer undo if needed).
        onLinkSponsor={
          has_linked_sponsor ? undefined : () => setLinkModalOpen(true)
        }
        onUnlinkSponsor={
          has_linked_sponsor ? () => setUnlinkConfirmOpen(true) : undefined
        }
      />
      <LinkForm5500SponsorModal
        open={link_modal_open}
        prospectName={prospect.name}
        onClose={() => setLinkModalOpen(false)}
        onSearch={(q) => searchForm5500Sponsors(q)}
        onLink={handleLink}
      />
      <ConfirmDialog
        open={unlink_confirm_open}
        title="Unlink Form 5500 sponsor?"
        body={
          "Fit score, carrier lineup, and change alerts will disappear"
          + " until you link a new sponsor."
        }
        confirmLabel="Unlink"
        variant="danger"
        onConfirm={handleUnlinkConfirm}
        onCancel={() => setUnlinkConfirmOpen(false)}
        testId="prospect-unlink-form5500-confirm"
      />
    </>
  );
}


function ContactsSection({ prospect_id }: { prospect_id: string }) {
  const query = useProspectContactsQuery(prospect_id);
  const create = useCreateProspectContactMutation(prospect_id);
  const [modal_open, setModalOpen] = useState(false);
  const [error_message, setErrorMessage] = useState<string | null>(null);

  function handleSubmit(input: Parameters<typeof create.mutate>[0]) {
    setErrorMessage(null);
    create.mutate(input, {
      onSuccess: () => setModalOpen(false),
      onError: (err) => {
        const detail = extractErrorDetail(err);
        setErrorMessage(detail ?? "Couldn't save contact.");
      },
    });
  }

  return (
    <section
      data-testid="prospect-contacts-section"
      className="rounded-md border border-border-subtle bg-raised p-4"
    >
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            Sales contacts
          </h3>
          <p className="text-xs text-text-secondary">
            People who work at this prospect.
          </p>
        </div>
        <button
          type="button"
          data-testid="prospect-add-contact-button"
          onClick={() => {
            setErrorMessage(null);
            setModalOpen(true);
          }}
          className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
        >
          + Add contact
        </button>
      </header>

      {query.isLoading ? (
        <DashboardLoadingState variant="card" />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load contacts"
          retry={() => query.refetch()}
        />
      ) : (query.data?.rows ?? []).length === 0 ? (
        <DashboardEmptyState
          title="No contacts yet"
          description="Add the people you've spoken to at this prospect."
        />
      ) : (
        <ul
          data-testid="prospect-contacts-list"
          className="flex flex-col divide-y divide-border-subtle"
        >
          {(query.data?.rows ?? []).map((c) => (
            <li
              key={c.contact_id}
              data-testid={`prospect-contact-${c.contact_id}`}
              className="flex items-baseline justify-between gap-3 py-2 text-sm"
            >
              <div className="flex flex-col">
                <span className="font-medium text-text-primary">
                  {c.display_name}
                  {c.is_primary && (
                    <span className="ml-2 rounded-full bg-teal-soft px-2 py-0.5 text-[10px] uppercase tracking-wide text-teal-strong">
                      Primary
                    </span>
                  )}
                </span>
                {c.title && (
                  <span className="text-xs text-text-tertiary">
                    {c.title}
                  </span>
                )}
              </div>
              <div className="flex flex-col text-right text-xs text-text-secondary">
                {c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}
                {c.phone && <span>{c.phone}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}

      <AddContactModal
        open={modal_open}
        isSubmitting={create.isPending}
        errorMessage={error_message}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </section>
  );
}


function OpportunitiesSection({ prospect }: { prospect: ProspectDetail }) {
  const query = useProspectOpportunitiesQuery(prospect.prospect_id);
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Renewal anchor falls back to "now" when the prospect itself
  // doesn't have a renewal cycle on file — the modal's BE call still
  // expects a (month, year). The producer can correct in-modal before
  // submit.
  const today = new Date();
  const preboundProspect = {
    prospect_id: prospect.prospect_id,
    prospect_name: prospect.name,
    renewal_month: prospect.renewal_month ?? today.getMonth() + 1,
    renewal_year: prospect.renewal_year ?? today.getFullYear(),
  };

  return (
    <section
      data-testid="prospect-opportunities-section"
      className="rounded-md border border-border-subtle bg-raised p-4"
    >
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            Opportunities
          </h3>
          <p className="text-xs text-text-secondary">
            Active pursuits and BOR takeovers at this prospect.
          </p>
        </div>
        <button
          type="button"
          data-testid="prospect-add-opportunity-cta"
          onClick={() => setAddModalOpen(true)}
          className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-recessed"
        >
          + New opportunity
        </button>
      </header>

      <AddOpportunityModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        preboundProspect={preboundProspect}
      />

      {query.isLoading ? (
        <DashboardLoadingState variant="card" />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load opportunities"
          retry={() => query.refetch()}
        />
      ) : (query.data?.rows ?? []).length === 0 ? (
        <DashboardEmptyState
          title="No opportunities yet"
          description="Open the first opportunity to start tracking the pursuit."
        />
      ) : (
        <ul
          data-testid="prospect-opportunities-list"
          className="flex flex-col divide-y divide-border-subtle"
        >
          {(query.data?.rows ?? []).map((o) => (
            <li
              key={o.opportunity_id}
              data-testid={`prospect-opportunity-${o.opportunity_id}`}
              className="flex items-baseline justify-between gap-3 py-2 text-sm"
            >
              <div className="flex flex-col">
                <Link
                  to={`/dashboard/sales-crm/opportunities/${encodeURIComponent(
                    o.opportunity_id,
                  )}`}
                  className="font-medium text-text-primary hover:underline"
                >
                  {o.name}
                </Link>
                <span className="text-xs text-text-tertiary">
                  {o.opportunity_type.replaceAll("_", " ")} ·{" "}
                  {o.stage.replaceAll("_", " ")}
                </span>
              </div>
              <span className="text-xs text-text-secondary">
                {o.renewal_month && o.renewal_year
                  ? `${MONTH_NAMES[o.renewal_month - 1]} ${o.renewal_year}`
                  : "—"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


function RelatedCommitmentsSection() {
  // Operational Commitment Tracker doesn't expose a sales-prospect
  // filter yet; once that surfaces, mount the existing
  // CommitmentList component here filtered by prospect_id. Empty
  // state holds the slot in the meantime so the page geometry
  // doesn't shift when commitments arrive.
  return (
    <section
      data-testid="prospect-related-commitments-section"
      className="rounded-md border border-border-subtle bg-raised p-4"
    >
      <header className="mb-2 flex flex-col gap-0.5">
        <h3 className="text-sm font-semibold text-text-primary">
          Related commitments
        </h3>
        <p className="text-xs text-text-secondary">
          Operational commitments linked to this prospect.
        </p>
      </header>
      <DashboardEmptyState
        title="No commitments linked"
        description={
          "Once an operational commitment is created from this "
          + "prospect (e.g. via a BeneAI confirmation), it shows up "
          + "here alongside your sales activities."
        }
      />
    </section>
  );
}


// ── Helpers ──────────────────────────────────────────────────────────────


function NotFoundView() {
  return (
    <div
      data-testid="prospect-detail-page"
      className="flex flex-col gap-5"
    >
      <DashboardEmptyState
        title="Prospect not found in this entity"
        description={
          "The prospect you're looking for doesn't exist or isn't "
          + "accessible in the active entity."
        }
        action={
          <Link
            to="/dashboard/sales-crm?tab=prospects"
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-recessed"
          >
            ← Back to Prospects
          </Link>
        }
      />
    </div>
  );
}


function extractErrorDetail(err: unknown): string | null {
  if (
    err
    && typeof err === "object"
    && "body" in err
  ) {
    const body = (err as { body?: { detail?: string } }).body;
    if (body?.detail) return body.detail.replaceAll("_", " ");
  }
  if (err instanceof Error && err.message) return err.message;
  return null;
}


